import type { WhatsAppTemplateVariableMapping } from './whatsappHub';

export type WhatsAppCampaignStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED';

export type WhatsAppCampaignType = 'BROADCAST' | 'AUTOMATED';

export type WhatsAppSendMode = 'MANUAL' | 'AUTO';

export type WhatsAppAutomationStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';

export type WhatsAppAnalysisSource = 'MANUAL' | 'AI_BACKEND' | 'RULES';

export type WhatsAppServiceVertical =
  | 'doctor_consultation'
  | 'health_blogs'
  | 'her_phases'
  | 'health_records'
  | 'medicine_pharmacy'
  | 'ambulance'
  | 'lab_hospital'
  | 'general_search';

export interface WhatsAppCampaignRuleConfig {
  minDwellTimeSec?: number;
  activeDwellTimeSec?: number;
  minIntentScore?: number;
  delayMinutes?: number;
  conversionCheckMinutes?: number;
  cooldownHours?: number;
  intentEvents?: string[];
  scoreRules?: {
    activeReadingPoints?: number;
    highIntentEventPoints?: number;
    repeatViewPoints?: number;
    repeatWindowMinutes?: number;
  };
  userFilters?: {
    cities?: string[];
    platforms?: string[];
  };
  globalCooldownScope?: 'campaign' | 'global';
}

export type WhatsAppRetryHeaderMediaSlot = {
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerFileName?: string | null;
};

export interface WhatsAppCampaignSendPolicy {
  enabled?: boolean;
  phaseSize?: number;
  sendWindowHours?: number;
  weeklyCapDays?: number;
  nonReaderExcludeDays?: number;
  excludeNonReaders?: boolean;
  enforceWeeklyCap?: boolean;
  includeOptOut?: boolean;
  retryHeaderMediaPlan?: Array<WhatsAppRetryHeaderMediaSlot | string | null> | null;
}

export interface WhatsAppCampaignAutomationStats {
  jobsEnqueued?: number;
  jobsSent?: number;
  jobsCancelled?: number;
  jobsFailed?: number;
  lastJobAt?: string | null;
}

export interface WhatsAppCampaignSendJob {
  id: string;
  campaignId: string;
  userId?: string | null;
  phone: string;
  templateName: string;
  bodyValues: string[];
  sendAt: string | null;
  status: string;
  cancelReason?: string | null;
  jobSource: string;
  analysisMeta?: Record<string, unknown> | null;
  sentAt?: string | null;
  lastError?: string | null;
  createdAt: string;
}

export interface WhatsAppCampaignMeta {
  audiences: string[];
  statuses: string[];
  messageCategories: string[];
  campaignTypes?: string[];
  sendModes?: string[];
  automationStatuses?: string[];
  analysisSources?: string[];
  serviceVerticals?: { id: string; label: string }[];
  intentEventsByService?: Record<string, string[]>;
  defaultRuleConfig?: WhatsAppCampaignRuleConfig;
}

export type WhatsAppTargetAudience =
  | 'ALL_USERS'
  | 'VENDORS'
  | 'DOCTORS'
  | 'AMBULANCE'
  | 'INACTIVE_USERS'
  | 'SINGLE_USER'
  | 'SINGLE_VENDOR'
  | 'SINGLE_DOCTOR'
  | 'CUSTOM_PHONES';

export type WhatsAppMessageCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

export interface WhatsAppRecipientPreview {
  recipientType: string;
  recipientId: string | null;
  name: string;
  phone: string;
  countryCode?: string;
  phoneNumber?: string;
  maskedPhone: string;
  email?: string | null;
  city?: string | null;
  source?: string;
  platform?: string | null;
  vendorKind?: string | null;
  profileType?: string | null;
  tags?: string[];
}

export interface WhatsAppCampaign {
  id: string;
  name: string;
  templateName: string;
  templateLanguageCode: string;
  bodyValues: string[];
  templateVariableMappings?: WhatsAppTemplateVariableMapping[];
  headerValues?: string[];
  messageCategory: WhatsAppMessageCategory;
  campaignType?: WhatsAppCampaignType;
  sendMode?: WhatsAppSendMode;
  automationStatus?: WhatsAppAutomationStatus | null;
  analysisSource?: WhatsAppAnalysisSource;
  serviceVertical?: WhatsAppServiceVertical | string | null;
  ruleConfig?: WhatsAppCampaignRuleConfig | null;
  analysisConfig?: Record<string, unknown> | null;
  automationStats?: WhatsAppCampaignAutomationStats | null;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerFileName?: string | null;
  targetAudience: WhatsAppTargetAudience;
  recipientMode: 'audience' | 'custom';
  targetUserId?: string | null;
  targetVendorId?: string | null;
  customPhones: string[];
  filterCity?: string | null;
  filterState?: string | null;
  searchName?: string | null;
  status: WhatsAppCampaignStatus;
  scheduledAt?: string | null;
  scheduleTimezone?: string | null;
  sentAt?: string | null;
  recipientCount: number;
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  totalRead: number;
  previewSnapshot?: {
    total: number;
    participants: WhatsAppRecipientPreview[];
  } | null;
  interaktMeta?: WhatsAppCampaignInteraktMeta | null;
  sendPolicy?: WhatsAppCampaignSendPolicy | null;
  retryTemplatePlan?: string[] | null;
  retryHeaderMediaPlan?: Array<WhatsAppRetryHeaderMediaSlot | string | null> | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppCampaignInteraktMeta {
  interaktApiCampaignId?: string | null;
  interaktApiCampaignName?: string | null;
  interaktApiCampaignCreatedAt?: string | null;
  interaktApiCampaignDashboardUrl?: string | null;
  sentRecipients?: unknown[];
  errors?: unknown[];
  error?: string;
  rcsTemplateName?: string | null;
  rcsFallbackTemplateName?: string | null;
}

export interface WhatsAppTemplateButton {
  type: string;
  text: string;
  url?: string | null;
  phoneNumber?: string | null;
  code?: string | null;
  deepLinkCampaignId?: string | null;
  deepLinkUrlType?: 'short' | 'full' | 'dynamic' | null;
  deepLinkParamBindings?: Record<string, string> | null;
}

export type WhatsAppTemplateSyncStatus = 'SYNCED' | 'INTERAKT_ONLY' | 'DB_ONLY' | 'UNKNOWN';

export interface WhatsAppTemplateVersionHistoryEntry {
  id: string;
  templateId?: string | null;
  templateName: string;
  version: number;
  changeType: 'CREATE' | 'UPDATE' | 'SYNC' | 'DELETE' | 'RESTORE';
  snapshot: Record<string, unknown>;
  changedBy?: string | null;
  changeNote?: string | null;
  createdAt: string;
}

export interface WhatsAppTemplateDeletionLog {
  id: string;
  templateId?: string | null;
  templateName: string;
  snapshot: Record<string, unknown>;
  deletedBy?: string | null;
  deleteReason?: string | null;
  restoredAt?: string | null;
  restoredBy?: string | null;
  createdAt: string;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  displayName?: string;
  category: string;
  languageCode: string;
  bodyPreview?: string;
  variableCount: number;
  isActive?: boolean;
  interaktTemplateId?: string | null;
  approvalStatus?: string | null;
  headerFormat?: string | null;
  headerText?: string | null;
  headerHandleFileUrl?: string | null;
  headerHandleFileName?: string | null;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
  interaktSyncedAt?: string | null;
  waTemplateId?: string | null;
  localTemplateId?: string | null;
  defaultVariableMappings?: WhatsAppTemplateVariableMapping[];
  serviceVertical?: string | null;
  inDatabase?: boolean;
  inInterakt?: boolean;
  interaktPresent?: boolean;
  syncStatus?: WhatsAppTemplateSyncStatus;
  currentVersion?: number | null;
  deletedAt?: string | null;
}

/** True when template is approved for sending (Interakt / Meta). */
export function isApprovedWhatsAppTemplate(t: WhatsAppTemplate): boolean {
  const status = String(t.approvalStatus ?? '').trim().toUpperCase();
  if (status) return status === 'APPROVED';
  return t.isActive === true;
}

/** Approved and active — use in template pickers (excludes inactive / missing on Interakt). */
export function isActiveWhatsAppTemplate(t: WhatsAppTemplate): boolean {
  if (t.isActive === false) return false;
  return isApprovedWhatsAppTemplate(t);
}

export interface InteraktWhatsAppTemplate {
  interaktId: string | null;
  name: string;
  displayName: string;
  languageCode: string;
  language: string;
  category: string;
  body: string;
  footer?: string | null;
  headerFormat?: string | null;
  approvalStatus?: string | null;
  variableCount: number;
  isApproved: boolean;
}

export const SERVICE_VERTICAL_LABELS: Record<string, string> = {
  doctor_consultation: 'Doctor consultation',
  health_blogs: 'Health blogs',
  medicine_pharmacy: 'Medicine & pharmacy',
  ambulance: 'Ambulance & emergency',
  lab_hospital: 'Lab tests & hospitals',
  general_search: 'General / search',
};

export const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  BROADCAST: 'Broadcast',
  AUTOMATED: 'Automated (event-based)',
};

export const SEND_MODE_LABELS: Record<string, string> = {
  MANUAL: 'Manual send',
  AUTO: 'Automatic (AI / rules)',
};

export const AUDIENCE_LABELS: Record<string, string> = {
  ALL_USERS: 'All users',
  VENDORS: 'Vendors',
  DOCTORS: 'Doctors',
  AMBULANCE: 'Ambulance',
  INACTIVE_USERS: 'Inactive (30d)',
  SINGLE_USER: 'One user',
  SINGLE_VENDOR: 'One vendor',
  SINGLE_DOCTOR: 'One doctor',
  CUSTOM_PHONES: 'Custom list',
};
